<template>
    <div class="firm-configurable-documents-form loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>

        <div v-show="!isLoading">
            <b-table v-show="documentTypes.length" :fields="tableFields" :items="tableItems" responsive>
                <template v-slot:cell(documentTypeName)="rowData">
                    <span :class="rowData.item.belongsToId ? 'ml-4' : ''">
                        {{ rowData.value }}
                    </span>
                    <csp-form-tooltip v-if="rowData.item.inheritedFrom" :tooltip="buildInheritanceHint(rowData)"/>
                    <csp-form-tooltip v-if="rowData.item.hasDeprecatedPrintConfig"
                        icon-class="inticon-print deprecation-warning"
                        tooltip="Es existiert eine alte Dokumentkonfiguration für Druck"/>
                    <csp-form-tooltip v-if="rowData.item.hasDeprecatedSignConfig"
                        icon-class="inticon-signature deprecation-warning"
                        tooltip="Es existiert eine alte Dokumentkonfiguration für Unterschrift"/>
                </template>

                <template v-slot:cell(documentName)="rowData">
                    <span :class="rowData.item.inheritedFrom ? 'inherited-document' : ''">
                        {{ buildDocumentName(rowData) }}
                    </span>
                </template>

                <template v-slot:cell(excludeDocument)="rowData">
                    <b-form-checkbox v-if="rowData.item.isUsableDocumentType && rowData.item.documentId"
                        v-model="rowData.item.excludeDocument"
                        :disabled="isExcludeDocumentReadonly(rowData)"
                        @change="updateExcludeDocument($event, rowData)">
                        <span :class="rowData.item.inheritedFrom ? 'inherited-document' : ''">
                            Dokument ausblenden
                        </span>
                    </b-form-checkbox>
                </template>

                <template v-slot:cell(actions)="rowData">
                    <div class="table-actions">
                        <a v-if="canShowFile(rowData)" :href="buildFilePreviewUrl(rowData)" target="_blank"
                            title="Anzeigen">
                            <i class="inticon-autoclaim_detail"/>
                        </a>
                        <a v-if="canShowFile(rowData)" :href="buildFileDownloadUrl(rowData)"
                            title="Datei herunterladen">
                            <i class="inticon-autoclaim_einzeldownload"/>
                        </a>
                        <a v-if="canDeleteFile(rowData)" href="#" title="Dokument Löschen"
                            @click.prevent="deleteFile(rowData)">
                            <i class="inticon-autoclaim_loschen"/>
                        </a>
                        <csp-firm-document-upload v-if="canUploadFile(rowData)" :context-id="contextId"
                            :context-type="contextType" :document-type-id="rowData.item.documentTypeId"
                            @upload-error="isLoading=false"
                            @upload-start="isLoading=true"
                            @upload-success="handleFileUploadSuccess()"/>
                    </div>
                </template>
            </b-table>

            <div v-show="documentTypes.length === 0 && !isLoading" class="collapse-body">
                <b-alert class="mb-0" :show="true" variant="info">
                    Noch keine Dateien vorhanden.
                </b-alert>
            </div>
        </div>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import DocumentOwnerTypeEnum from '@js/enums/document/DocumentOwnerTypeEnum';
    import '@desktop/scss/components/firm-configurable-documents.scss';
    import FirmDocumentUpload from '@desktop/components/firm/FirmDocumentUpload.vue';

    const container = Container.getInstance();
    const flashMessenger = container.getFlashMessenger();
    const firmRepository = container.getFirmRepository();

    export default {
        name: 'FirmConfigurableDocumentsForm',
        components: {
            'csp-firm-document-upload': FirmDocumentUpload,
        },
        props: {
            // If of context with type defined below
            contextId: {
                type: Number,
                required: true,
            },
            // Type must be one of [firm|firmGroup|firmAssociation|reseller]
            contextType: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                tableFields: [
                    {
                        key: 'documentTypeName',
                        label: 'Dokumenttyp',
                    },
                    {
                        key: 'documentName',
                        label: 'Dokumentname',
                    },
                    {
                        key: 'excludeDocument',
                        label: 'Nutzung',
                    },
                    {
                        key: 'actions',
                        label: 'Aktionen',
                    },
                ],
                isLoading: true,
                documentTypes: [],
                assignedDocuments: [],
                inheritedDocuments: [],
            };
        },
        computed: {
            hasDocumentTypes() {
                return this.documentTypes.length > 0;
            },
            documentTypesTree() {
                // ATTENTION: We only build the tree for one level of documentTypes
                if (!this.documentTypes) {
                    return [];
                }

                let documentTypes = this.documentTypes;

                let rootTypes = [];

                // Collect root types
                documentTypes = documentTypes.filter(function(type) {
                    if (type.attributes.belongsToId == null) {
                        rootTypes.push(type);
                        // exclude current type from remaining document types
                        return false;
                    }
                    return true;
                })

                // Add child types to root types
                rootTypes.forEach(function(rootType, rootIndex) {
                    rootTypes[rootIndex].childTypes = [];
                    documentTypes = documentTypes.filter(function(type) {
                        if (rootType.id === type.attributes.belongsToId) {
                            rootTypes[rootIndex].childTypes.push(type);
                            // exclude current type from remaining document types
                            return false;
                        }
                        // include current type in remaining document types
                        return true;
                    });
                })

                return rootTypes;
            },
            tableItems() {
                const documentTypesTree = this.documentTypesTree;

                // sort root nodes
                documentTypesTree.sort((type1, type2) => type1.attributes.order - type2.attributes.order);

                // sort child nodes of each root node
                documentTypesTree.forEach(rootType => {
                    rootType.childTypes.sort(
                        (type1, type2) => type1.attributes.order - type2.attributes.order
                    );
                });

                // flatten the tree
                const documentTypes = [];
                documentTypesTree.forEach(rootType => {
                    documentTypes.push(rootType);
                    rootType.childTypes.forEach(childType => {
                        documentTypes.push(childType);
                    });
                });

                // build table items by matching document types with documents
                const tableItems = [];
                documentTypes.forEach(documentType => {
                    const documents = this.getDocumentsForType(documentType);
                    if (documents.length > 0) {
                        // add an entry for each document of the current document type
                        documents.forEach(document => {
                            tableItems.push({
                                documentId: document.id,
                                ...documentType.attributes,
                                ...document.attributes,
                            });
                        });
                    } else {
                        // add single entry for document type without document data
                        tableItems.push({
                            documentTypeId: documentType.id,
                            ...documentType.attributes,
                        });
                    }
                });


                return tableItems;
            },
        },
        mounted() {
            this.loadDocuments();
        },
        methods: {
            loadDocuments() {
                this.isLoading = true;

                firmRepository.getAvailableDocumentTypes(this.contextId, this.contextType).then((response) => {
                    this.documentTypes = response.data.attributes.documentTypes.data;
                    this.assignedDocuments = response.data.attributes.assignedDocuments.data;
                    this.inheritedDocuments = response.data.attributes.inheritedDocuments.data;
                }).finally(() => {
                    this.isLoading = false
                });
            },
            getDocumentsForType(documentType) {
                const assignedDocuments = this.assignedDocuments.filter(function(document) {
                    return document.attributes.documentTypeId === documentType.id;
                });
                const inheritedDocuments = this.inheritedDocuments.filter(function(document) {
                    return document.attributes.documentTypeId === documentType.id;
                });
                return assignedDocuments.concat(inheritedDocuments);
            },
            buildDocumentName(rowData) {
                const item = rowData.item;

                if (!item.isUsableDocumentType) {
                    // there can be no document of this type
                    return '';
                }

                if (!item.documentId) {
                    return 'kein Dokument hinterlegt';
                }

                let documentName = item.fileName;
                // include document label if the document has a custom label that differs from the document type name
                if (item.label && item.label !== item.documentTypeName) {
                    documentName += ` (${item.label})`;
                }
                return documentName;
            },
            buildInheritanceHint(rowData) {
                const inheritedFrom = rowData.item.inheritedFrom;
                switch (inheritedFrom) {
                case DocumentOwnerTypeEnum.FIRM_GROUP:
                    return 'von Firmengruppe geerbt';
                case DocumentOwnerTypeEnum.FIRM_ASSOCIATION:
                    return 'von Zuordnung geerbt';
                case DocumentOwnerTypeEnum.RESELLER:
                    return 'von Reseller geerbt';
                default:
                    return 'geerbt';
                }
            },
            isExcludeDocumentReadonly(rowData) {
                // field is readonly if the document is excluded and either inherited
                // or an empty document (= has no fileName) that exists to exclude an inherited document
                return rowData.item.excludeDocument && (rowData.item.inheritedFrom || !rowData.item.fileName);
            },
            canShowFile(rowData) {
                return rowData.item.documentId;
            },
            canDeleteFile(rowData) {
                return rowData.item.documentId && !rowData.item.inheritedFrom;
            },
            canUploadFile(rowData) {
                return rowData.item.isUsableDocumentType;
            },
            buildFilePreviewUrl(rowData) {
                return firmRepository.buildDocumentPreviewUrl(rowData.item.documentId);
            },
            buildFileDownloadUrl(rowData) {
                return firmRepository.buildDocumentDownloadUrl(rowData.item.documentId);
            },
            deleteFile(rowData) {
                if (!confirm('Wollen Sie das Dokument wirklich löschen?')) {
                    return;
                }

                this.isLoading = true;
                firmRepository.deleteDocument(rowData.item.documentId)
                    .then((response) => {
                        if (!response.data) {
                            flashMessenger.addError(this.$t('document delete error'));
                            return;
                        }

                        flashMessenger.addSuccess(this.$t('document delete success'));
                    }).finally(() => {
                        this.loadDocuments();
                    });
            },
            updateExcludeDocument(event, rowData) {
                this.isLoading = true;
                firmRepository.handleDocumentExcludeToggle(
                    rowData.item.documentTypeId,
                    this.contextId,
                    this.contextType,
                    event,
                    // do not submit the documentId of an inherited document
                    rowData.item.inheritedFrom ? null : rowData.item.documentId
                ).finally(() => {
                    this.loadDocuments();
                });
            },
            handleFileUploadSuccess() {
                this.loadDocuments();
            },
        },
    }
</script>
